import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from '../../../../services/commande-client/data.service';
import { HeaderService } from '../../../../services/header/header.service';
import { ExportService } from '../../shared/export.service';
import { ToastrService } from 'ngx-toastr';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isAvoirTableRow, isFactureTableRowExcludingAvoirs, resolveFactureStatutTitre } from '../../../../services/document/document-avoir-row.util';
import { LogicielCaisseTicketService } from '../../../../services/logiciel-caisse/logiciel-caisse-ticket.service';
import { LogicielCaisseAuthService } from '../../../../services/logiciel-caisse/logiciel-caisse-auth.service';
import {
  FACTURE_LIST_DISPLAY_DEFAULTS,
  FACTURE_LIST_DISPLAY_FIELDS,
  FactureListDisplayService,
  FactureListDisplaySettings,
} from '../../../../services/facture/facture-list-display.service';

@Component({
  selector: 'app-facture',
  templateUrl: './facture.component.html',
  styleUrls: ['./facture.component.scss']
})
export class FactureComponent implements OnInit, OnDestroy {

    currentStatus: string = 'tous';
    statuses: string[] = ['tous', 'brouillon', 'paye', 'non_payé', 'annule', 'partPaye', 'consolidation'];
    currentUser: any = null;
    userId: number | null = null;
    stationId: number | null = null;
    /** IDs facture ERP liés à au moins un ticket de caisse. */
    private consolidationFactureIds = new Set<number>();
    private dataSub?: Subscription;
    private totalsSub?: Subscription;
    private linksSub?: Subscription;

    /** Panneau paramètres d'affichage (même UX que Paramètres document). */
    isDisplaySettingsOpen = false;
    displayDraft: FactureListDisplaySettings = { ...FACTURE_LIST_DISPLAY_DEFAULTS };
    readonly displayFields = FACTURE_LIST_DISPLAY_FIELDS;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private dataService: DataService,
        private headerService: HeaderService,
        private exportService: ExportService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
        private lcTicketService: LogicielCaisseTicketService,
        private lcAuthService: LogicielCaisseAuthService,
        private listDisplay: FactureListDisplayService
    ) {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        this.userId = this.currentUser?.id || null;
        this.stationId = this.currentUser?.stations?.[0]?.id || null;
    }

    ngOnInit(): void {
        this.displayDraft = this.listDisplay.loadForUser(this.userId);
        this.dataSub = this.dataService.factureTableData$.subscribe(() => {
            setTimeout(() => this.cdr.detectChanges(), 0);
        });
        this.totalsSub = this.dataService.factureTotals$.subscribe(() => {
            setTimeout(() => this.cdr.detectChanges(), 0);
        });
        this.refreshConsolidationLinks();
        this.headerService.setConfig({
            title: 'Facture',
            buttons: [
                {
                    label: 'Exporter',
                    type: 'dropdown',
                    dropdownItems: [
                        {
                            label: 'PDF',
                            action: () => this.exportService.triggerPDF(),
                            icon: '../../../assets/images/logo/pdf_logo.png'
                        },
                        {
                            label: 'CSV',
                            action: () => this.exportToCSV(),
                            icon: '../../../assets/images/logo/csv_logo.png'
                        }
                    ]
                }
            ]
        });
        this.route.firstChild?.url.subscribe(segments => {
            const path = segments.length > 0 ? segments[0].path : 'tous';
            if (path.toLowerCase() === 'part-paye') {
                this.currentStatus = 'partPaye';
            } else {
                this.currentStatus = path.toLowerCase();
            }
        });
    }

    ngOnDestroy(): void {
        this.dataSub?.unsubscribe();
        this.totalsSub?.unsubscribe();
        this.linksSub?.unsubscribe();
    }

    openDisplaySettings(): void {
        this.displayDraft = { ...this.listDisplay.settings };
        this.isDisplaySettingsOpen = true;
    }

    closeDisplaySettings(): void {
        this.isDisplaySettingsOpen = false;
    }

    onDisplayFieldChange(): void {
        // Aperçu live dans le tableau
        this.listDisplay.setAll(this.displayDraft);
    }

    saveDisplaySettings(): void {
        this.listDisplay.saveForUser(this.userId, this.displayDraft);
        this.toastr.success('Préférences d\'affichage enregistrées', 'Facture');
        this.closeDisplaySettings();
    }

    private refreshConsolidationLinks(): void {
        if (!this.lcAuthService.isAuthenticated()) {
            this.consolidationFactureIds = new Set();
            return;
        }
        this.linksSub?.unsubscribe();
        this.linksSub = this.lcTicketService.getErpFactureLinks().pipe(
            catchError(() => of([] as number[]))
        ).subscribe((ids) => {
            this.consolidationFactureIds = new Set(ids || []);
            this.cdr.detectChanges();
        });
    }

    private getVentePrefix(): string {
        const url = this.router.url || '';
        return url.includes('/vente-negoce/') ? '/vente-negoce' : '/vente';
    }

    filterByStatus(status: string) {
        const urlPath = status === 'partPaye' ? 'part-paye' : status.toLowerCase();
        this.currentStatus = status === 'partPaye' ? 'partPaye' : status.toLowerCase();
        const mappedStatus = this.mapPathToStatus(status);
        this.dataService.loadByTypeAndStatus('facture', mappedStatus === 'Consolidation' ? 'Tous' : mappedStatus);
        this.router.navigate([`${this.getVentePrefix()}/facture/${urlPath}`]);
    }


    private mapPathToStatus(path: string): string {
        const statusMap: { [key: string]: string } = {
            tous: 'Tous',
            brouillon: 'Brouillon',
            paye: 'Payé',
            non_payé: 'Non payé',
            annule: 'Annulé',
            partpaye: 'Partiellement payé',
            'part-paye': 'Partiellement payé',
            consolidation: 'Consolidation',
        };
        return statusMap[path.toLowerCase()] || 'Tous';
    }

    getStatusCount(status: string): number {
        const data = this.dataService.getFactureTableData() || [];
        const factures = data.filter((d: any) => this.isFactureDocument(d));

        if (status === 'Consolidation') {
            return factures.filter((d: any) => {
                const id = Number(d?.id ?? d?.fullData?.id ?? d?.document_id);
                return Number.isFinite(id) && id > 0 && this.consolidationFactureIds.has(id);
            }).length;
        }

        if (status === 'Tous') {
            const totalFactureOnly = this.factureStatuses.reduce(
                (sum, s) => sum + this.dataService.getFactureTotalItems(s),
                0
            );
            if (totalFactureOnly > 0) {
                return Math.max(0, totalFactureOnly);
            }
            return factures.length;
        }

        const totalFromApi = this.dataService.getFactureTotalItems(status);
        if (totalFromApi > 0) {
            return totalFromApi;
        }
        return factures.filter((i: any) => this.statusMatches(i, status)).length;
    }

    private statusMatches(item: any, status: string): boolean {
        const s = resolveFactureStatutTitre(item);
        if (status === 'Payé') {
            return s === 'Payé';
        }
        if (status === 'Annulé') {
            return s === 'Annulé';
        }
        return s === status || s === resolveFactureStatutTitre({ status });
    }

    private isAvoirDocument(d: any): boolean {
        return isAvoirTableRow(d);
    }

    private readonly factureStatuses = ['Brouillon', 'Payé', 'Non payé', 'Partiellement payé', 'Annulé'];

    private isFactureDocument(d: any): boolean {
        return isFactureTableRowExcludingAvoirs(d, this.factureStatuses);
    }

    private exportToCSV(): void {
        const data = this.dataService.getFactureTableData() || [];
        const factures = data.filter((d: any) => this.isFactureDocument(d));
        const headers = ['Référence', 'Client', 'Date', 'Revenu', 'Matricule', 'Statut'];
        const rows = factures.map(item => [
            `"${(item.reference || '').replace(/"/g, '""')}"`,
            `"${(item.clientName || '').replace(/"/g, '""')}"`,
            `"${(item.date || '').replace(/"/g, '""')}"`,
            `"${(item.revenue || '').replace(/"/g, '""')}"`,
            `"${(item.matricule || '').replace(/"/g, '""')}"`,
            `"${(item.status || '').replace(/"/g, '""')}"`
        ]);
        const csvContent = headers.join(',') + '\n' + rows.map(row => row.join(',')).join('\n');
        this.exportService.downloadCSV(csvContent, 'facture.csv');
    }

}
